import { CalendarDays } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { commitDateText, dateKeyShortcut, displayDate } from '@/lib/dates';
import { cn } from '@/lib/utils';

/**
 * DateInput (document 05): shows dd/MM/yyyy, accepts typed dates and the QBO shortcuts
 * (T today, +/- day, W/K week, M/H month, Y/R year); the calendar button opens the browser picker.
 * Unreadable text goes back to the last date; `clearable` (optional dates) lets an emptied field
 * clear the value to ''.
 */
export function DateInput({
  value,
  onChange,
  className,
  invalid,
  clearable = false,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (iso: string) => void;
  invalid?: boolean;
  /** An optional date: emptying the field sets ''. */
  clearable?: boolean;
}) {
  const t = useT();
  const [text, setText] = useState(displayDate(value));
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => setText(displayDate(value)), [value]);

  return (
    <div className={cn('relative', className)}>
      <input
        {...props}
        type="text"
        inputMode="numeric"
        placeholder={t('ui.datePlaceholder')}
        value={text}
        aria-invalid={invalid || undefined}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
          const next = dateKeyShortcut(e.key, text, value);
          if (next) {
            e.preventDefault();
            onChange(next);
          }
        }}
        onBlur={(e) => {
          const iso = commitDateText(text, value, clearable);
          if (iso !== value) onChange(iso);
          setText(displayDate(iso)); // "1/2/26" shows as 01/02/2026 even when the date is unchanged
          props.onBlur?.(e);
        }}
        className="h-9 w-full rounded-md border border-input bg-card px-3 pe-9 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={t('ui.openCalendar')}
        className="absolute end-1 top-1 rounded p-1.5 text-muted-foreground hover:bg-accent"
        onClick={() => picker.current?.showPicker?.()}
      >
        <CalendarDays className="size-4" />
      </button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute bottom-0 end-0 size-0 opacity-0"
        value={value}
        onChange={(e) => e.target.value && onChange(e.target.value)}
      />
    </div>
  );
}
